import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import ProductService from "../../api/ProductService";
import { AdminSearch } from "../../components/AdminSearch/AdminSearch";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { Info } from "../../components/Info/Info";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import { NoData } from "../../components/NoData/NoData";
import { RelatedProductModal } from '../../components/RelatedProductsModal/RelatedProductModal';
import { Table } from "../../components/Table/Table";
import { useScreenSize } from "../../hooks/useScreenSize";
import { LeftArrowIcon } from "../../icons/LeftArrowIcon/LeftArrowIcon";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { RootState } from "../../store";
import { productManagementActions } from '../../store/product-management-slice';
import styles from "../../styles/product-management.module.css";
import { Product } from "../../types/product";
import { Meta } from "../../types/response";
import { TableColumn } from "../../types/table";

import { AddProductModal } from "./AddProductModal";

export const ProductManagement = () => {
    const dispatch = useDispatch();
    const isAdmin = useSelector((state: RootState) =>
        state.user.roles.some((role) => role === "admin" || role === "kunde.admin")
    );
    const { t } = useTranslation();
    const { isDesktop } = useScreenSize();

    const [products, setProducts] = useState<Product[]>([]);
    const [meta, setMeta] = useState<Meta>({
        total: 0,
        count: 0,
        perPage: 0,
        currentPage: 1,
        totalPages: 1
    });
    const [loading, setLoading] = useState(false);
    const [failedToFetch, setFailedToFetch] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
    const [productAdded, setProductAdded] = useState(false);
    const [relatedProducts, setRelatedProducts] = useState<any[]>([]);
    const [isRelatedProductModalOpen, setIsRelatedProductModalOpen] = useState(false);

    const columns: TableColumn[] = [
        { name: "Nr.", key: "referenceNumber", render: (value: any, row: Product) => <p onClick={() => handleEdit(row)}>{value || '-'}</p> },
        { name: "Name", key: "name", render: (value: any, row: Product) => <p onClick={() => handleEdit(row)}>{value || '-'}</p> },
        { name: "Einheit", key: "salesUnit", render: (value: any) => value?.name || '-' },
        { name: "Preis (€)", key: "price", render: (value: any) => value ? (value?.price / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-" },
        { name: "Zubehör", key: "relatedProducts", render: (value: any, row: Product) => <p onClick={() => { setRelatedProducts(row.relatedProducts); if (row.relatedProducts.length) setIsRelatedProductModalOpen(true) }}>{value.length ? `${value.length} Zubehör` : <span>-</span>}</p> },
        { name: "", key: "visibility", render: (_value: any, row: Product) => (row.isHidden ? <VisibilityOutlinedIcon htmlColor='#069AA9' data-visibility-icon onClick={() => handleToggleVisibility(row, false)} /> : <VisibilityOffOutlinedIcon htmlColor='#8493AB' data-visibility-icon onClick={() => handleToggleVisibility(row, true)} />) }
    ];


    const handleBackButtonClicked = () => {
        window.history.back();
    };

    const handleInputChange = (event: any) => {
        setSearchTerm(event.target.value);
    };

    const handleToggleVisibility = async (product: Product, isHidden: boolean) => {
        try {
            await ProductService.ToggleProductVisibility(product.id, { isHidden });
            setProducts((prevProducts) =>
                prevProducts.map((item) =>
                    item.id === product.id
                        ? { ...item, isHidden }
                        : item
                )
            );
        } catch (error) {
            console.log(error);
        }
    };

    const getProducts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await ProductService.GetProducts({ perPage: 20, ...(searchTerm === '' && { page: currentPage }), search: searchTerm });
            setProducts(response.data);
            setMeta(response.meta);
        } catch (error) {
            console.log(error);
            setFailedToFetch(true);
        }
        finally {
            setLoading(false);
            setFailedToFetch(false);
        }
    }, [currentPage, searchTerm]);

    const getProduct = async (productId: string) => {
        try {
            const response = await ProductService.GetProduct(productId);
            dispatch(productManagementActions.setEditProduct({
                ...response.data,
                price: {
                    price: response.data.price?.price ? (response.data.price?.price / 100).toLocaleString("de-DE") : null,
                    purchasePrice: response.data.price?.purchasePrice ? (response.data.price?.purchasePrice / 100).toLocaleString("de-DE") : null
                },
                relatedProducts: response.data.relatedProducts.map((el: any) => el.id),
                extendedPrices: response.data.extendedPrices.map((el: any) => ({ price: el.price ? (el.price / 100).toLocaleString("de-DE") : null, customerId: el.customerId, customerName: el.customerName }))
            }));
            dispatch(productManagementActions.setRelatedProductDetails(response.data.relatedProducts));
            dispatch(productManagementActions.setDocuments(response.includes?.documents || []));

        } catch (error) {
            console.log(error);
        }
    }

    const handleEdit = (el: Product) => {
        getProduct(el.id);
        setIsAddProductModalOpen(true);
    }

    useEffect(() => {
        getProducts();
        setProductAdded(false)
    }, [currentPage, productAdded]);

    useEffect(() => {
        const debouncedSearch = debounce(async () => {
            getProducts();
            setCurrentPage(1);
        }, 1000);
        debouncedSearch();
        return () => debouncedSearch.cancel();
    }, [searchTerm]);

    return (
        <AppLayout
            withBackButton={true}
            hideHeader={isAdmin}
            onGoBack={handleBackButtonClicked}>
            <div className={styles["material-um"]} id="scrollable-wrapper">
                <div className={styles["material-um__heading"]}>
                    <div className={styles["material-um__back"]}>
                        <LeftArrowIcon
                            handleBackButtonClicked={handleBackButtonClicked}
                        />
                        <p className={styles["material-um__title"]}>
                            {t("MaterialManagement")}
                        </p>
                    </div>

                    {isDesktop && (
                        <CustomButton color="gray" icon="plus" iconPosition="left" customClass={styles["material-um__new-material"]}
                            onClick={() => setIsAddProductModalOpen(true)}>
                            {t("NewMaterial")}
                        </CustomButton>

                    )}
                </div>

                <div className={styles["material-um__content"]}>

                    <div className={styles.table}>
                        <AdminSearch searchTerm={searchTerm} handleInputChange={handleInputChange} />
                        {loading ?
                            <LoadingSpinner loading={loading} />
                            :

                            (failedToFetch ?
                                <Info type="error">{t("Data retrieval failed")}</Info>
                                :
                                (products.length === 0 ?
                                    <NoData>{t("NoEntries")}</NoData>
                                    :
                                    <Table columns={columns} data={products} meta={meta} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                                )
                            )
                        }
                    </div>
                </div>
            </div>
            <AddProductModal
                isOpen={isAddProductModalOpen}
                onClose={() => setIsAddProductModalOpen(false)}
                onProductAdded={() => setProductAdded(true)}
            />
            <RelatedProductModal data={relatedProducts} isOpen={isRelatedProductModalOpen} onClose={() => setIsRelatedProductModalOpen(false)} />
        </AppLayout>
    );
};